import { useEffect, useState } from 'react';
import { type MediaType } from '../../types';
import { LAYOUT, MEDIA_TYPE, SESSION_TYPE } from '../../utils';
import { type HumanParticipant } from '../../utils/schema/human-participant';
import { useBaseStore } from '../base';
import { useConferenceStore } from '../conference';
import { useConfigStore } from '../config';
import { useConnectionStore } from '../connection';
import {
  getMainParticipantFromState,
  type Participant,
  useAllParticipants,
  useParticipantStore,
} from '../participants';
import {
  getLocalTrack,
  useLocalTrack,
  useTrackByParticipantId,
  useTracksStore,
} from '../tracks';

export const useHideMuteAudioButton = () => {
  const hideMuteAudioButton = useConfigStore(
    state => state.hideToggleAudioButton,
  );
  const enableCompanionMode = useConfigStore(
    state => state.enableCompanionMode,
  );

  return hideMuteAudioButton || enableCompanionMode;
};

export const useHideToggleVideoButton = () => {
  const hideToggleVideoButton = useConfigStore(
    state => state.hideToggleVideoButton,
  );
  const type = useConfigStore(state => state.sessionType);

  return hideToggleVideoButton || type !== SESSION_TYPE.VIDEO;
};

export const useIsConferenceJoined = () => {
  const conferenceStatus = useConferenceStore(state => state.conferenceStatus);
  return conferenceStatus === 'joined' || conferenceStatus === 'restored';
};

export const useHideScreenSharingButton = () => {
  const hideScreenSharingButton = useConfigStore(
    state => state.hideScreenSharingButton,
  );
  const isAndroid = useBaseStore(state => state.sdkPlatform === 'android');
  const isDesktopSharingEnabled = useBaseStore(
    state => state.isDesktopSharingEnabled,
  );
  const isConferenceJoined = useIsConferenceJoined();
  const isMobileSDK = useBaseStore.getState().isMobileSDK();

  return (
    hideScreenSharingButton ||
    isMobileSDK ||
    !isConferenceJoined ||
    (isAndroid ? false : !isDesktopSharingEnabled)
  );
};

export const useHideSwitchCameraButton = () => {
  const videoTrack = getLocalTrack(MEDIA_TYPE.VIDEO);
  const hideSwitchCameraButton = useConfigStore(
    state => state.hideSwitchCameraButton,
  );
  return hideSwitchCameraButton || !videoTrack;
};

export const useHideChatButton = () => {
  const hideChatButton = useConfigStore(state => state.hideChatButton);
  return hideChatButton;
};

export const useLocalParticipant = <T = HumanParticipant>(
  selector?: (local: HumanParticipant) => T,
): T => {
  return useParticipantStore(state =>
    selector ? selector(state.localParticipant) : (state.localParticipant as T),
  );
};

export const useScreenShareRunning = () => {
  const screenShareTrack = useLocalTrack(MEDIA_TYPE.SCREENSHARE);
  return screenShareTrack?.originalTrack && !screenShareTrack.muted;
};

export const useTrackMuted = (mediaType: MediaType, participantId?: string) => {
  const localParticipant = useLocalParticipant();
  const track = useTrackByParticipantId(
    participantId ?? localParticipant.pid,
    mediaType,
  );
  return track ? Boolean(track.muted) : true;
};

export const useMainParticipant = (): Participant => {
  const pinnedParticipant = useParticipantStore(
    state => state.pinnedParticipant,
  );
  const participants = useParticipantStore(state => state.participants);
  const virtualParticipants = useParticipantStore(
    state => state.virtualParticipants,
  );
  const dominantSpeakers = useParticipantStore(state => state.dominantSpeakers);
  const localParticipant = useLocalParticipant();

  return getMainParticipantFromState({
    pinnedParticipant,
    participants,
    virtualParticipants,
    dominantSpeakers,
    localParticipant,
  });
};

export const getMainParticipant = (): Participant => {
  const state = useParticipantStore.getState();
  return getMainParticipantFromState({
    pinnedParticipant: state.pinnedParticipant,
    participants: state.participants,
    virtualParticipants: state.virtualParticipants,
    dominantSpeakers: state.dominantSpeakers,
    localParticipant: state.localParticipant,
  });
};

export const useIsReconnecting = () => {
  const connectionStatus = useConnectionStore(state => state.connectionStatus);
  const conferenceStatus = useConferenceStore(state => state.conferenceStatus);
  const isP2P = useConferenceStore(state => state.p2p);
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const controller = new AbortController();
    const { signal } = controller;
    window.addEventListener('online', () => setIsOnline(true), { signal });
    window.addEventListener('offline', () => setIsOnline(false), { signal });
    return () => controller.abort();
  }, []);

  const interrupted =
    connectionStatus === 'connected' && conferenceStatus === 'interrupted';

  // In P2P calls, only show reconnecting if the local user's network is down.
  // Otherwise the interruption is from the remote peer dropping.
  if (isP2P && interrupted && isOnline) {
    return false;
  }

  return interrupted;
};

export const useHideRecordingButton = () => {
  const hideRecordingButton = useConfigStore(
    state => state.hideRecordingButton,
  );
  const isConferenceJoined = useIsConferenceJoined();
  const localParticipant = useLocalParticipant();
  return (
    hideRecordingButton ||
    !isConferenceJoined ||
    localParticipant.role !== 'moderator'
  );
};

export const useHideVirtualBackgroundButton = () => {
  const hideVirtualBackgroundButton = useConfigStore(
    state => state.hideVirtualBackgroundButton,
  );
  const videoTrack = getLocalTrack(MEDIA_TYPE.VIDEO);
  const isBlurSupported = useBaseStore(state => state.isBlurSupported);
  return (
    hideVirtualBackgroundButton ||
    !videoTrack?.originalTrack ||
    !isBlurSupported
  );
};

export const useLayout = () => {
  const layout = useBaseStore(state => state.layout);
  return layout;
};

export const useEnableParticipantContextMenu = () => {
  const enableParticipantContextMenu = useConfigStore(
    state => state.enableParticipantContextMenu,
  );
  const isPIPLayoutEnabled = useBaseStore(state => state.isPIPLayoutEnabled);
  const layout = useLayout();
  const isSpotlightLayout = layout === LAYOUT.SPOTLIGHT;
  if (isSpotlightLayout || isPIPLayoutEnabled) {
    return false;
  }
  return enableParticipantContextMenu;
};

export const useRaisedHandTimestamp = (
  participantId?: string,
  type: Participant['type'] = 'human',
) => {
  const localParticipant = useLocalParticipant();
  const raisedHandTimestamp = useConferenceStore(state =>
    state.raiseHandMap.get(participantId ?? localParticipant.pid),
  );
  return type === 'human' ? raisedHandTimestamp : undefined;
};

export const useIsParticipantPinned = (
  participantId: string,
  type: Participant['type'],
) => {
  const pinnedParticipant = useParticipantStore(
    state => state.pinnedParticipant,
  );
  if (
    pinnedParticipant &&
    pinnedParticipant.pid === participantId &&
    pinnedParticipant.type === type
  ) {
    return true;
  }
  return false;
};

export const useOrderedParticipants = () => {
  const participants = useAllParticipants();
  const raiseHandMap = useConferenceStore(state => state.raiseHandMap);
  const orderedParticipants = participants.sort((a, b) => {
    const aRaisedHand = raiseHandMap.get(a.pid);
    const bRaisedHand = raiseHandMap.get(b.pid);
    if (aRaisedHand && !bRaisedHand) {
      return -1;
    }
    if (!aRaisedHand && bRaisedHand) {
      return 1;
    }
    return a.name.localeCompare(b.name);
  });
  return orderedParticipants;
};

export const useNonRecoverableError = () => {
  const conferenceError = useConferenceStore(state => state.conferenceError);
  const connectionError = useConnectionStore(state => state.error);

  return conferenceError || connectionError;
};

export const useIsSwitchCameraEnabledBrowser = () => {
  const isMobileBrowser = useBaseStore(state => state.isMobileBrowser);
  const videoTrack = getLocalTrack(MEDIA_TYPE.VIDEO);

  return isMobileBrowser && videoTrack?.originalTrack;
};

export const useIsAudioInputSelectionSupported = () => {
  const hasAudioPermission = useTracksStore(state => state.permissions.audio);
  const audioInputDevices = useBaseStore(state => state.audioInputDevices);
  const isAudioInputSelectionSupported =
    hasAudioPermission && audioInputDevices.length > 0;
  return isAudioInputSelectionSupported;
};

export const useIsVideoInputSelectionSupported = () => {
  const hasVideoPermission = useTracksStore(state => state.permissions.video);
  const videoInputDevices = useBaseStore(state => state.videoInputDevices);
  const isVideoInputSelectionSupported =
    hasVideoPermission && videoInputDevices.length > 0;
  return isVideoInputSelectionSupported;
};

export const useShouldMirrorLocalVideo = () => {
  const mirrorLocalVideo = useBaseStore(state => state.mirrorLocalVideo);
  const cameraFacing = useBaseStore(state => state.cameraFacing);
  return cameraFacing === 'user' && mirrorLocalVideo;
};
